/* ============================================================
   Maštarije — custom product personalizer (replaces Textify).
   Ported near-verbatim from mastarije-theme/assets/mst-personalizer.js.

   <mst-personalizer data-form-id="..."> wraps personalization
   fields. Fields submit as line-item properties[...] via the
   HTML form="" association, so they appear in cart + order + email.

   Features:
   - Dynamic repeatable groups ("Dodaj curu / Dodaj člana") with
     add/remove and automatic re-numbering of property keys.
   - Required validation that blocks Add-to-cart (capture-phase
     submit listener → runs before <mst-product-form>'s fetch handler
     and before the sticky mobile bar's triggered submit).
   - Character counters for fields with a max length.
   - Strips empty OPTIONAL fields on submit so the order stays clean.
   - A11y: aria-invalid on empty required fields + role="alert"
     message ([data-mst-pers-error]) announced to screen readers.
   ============================================================ */
(function () {
  class MstPersonalizer extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId || '';
      this.form =
        (this.formId && document.getElementById(this.formId)) ||
        this.closest('form');
      this.errorEl = this.querySelector('[data-mst-pers-error]');

      this.querySelectorAll('.mst-nl').forEach((g) => this.initGroup(g));
      this.querySelectorAll('[data-maxlength]').forEach((i) => this.initCounter(i));

      if (this.form) {
        // The form ships WITHOUT novalidate so native constraint validation
        // guards the no-JS / pre-hydration path. Once this element is live,
        // take over with the styled capture-phase validation below.
        this.form.noValidate = true;
        // capture phase → runs before the theme's product-form fetch handler
        this.form.addEventListener('submit', (e) => this.onSubmit(e), true);
      }

      // Variant-conditional blocks ([data-mst-cond-option/-value]): shown
      // only while the matching option value is selected. Hidden blocks get
      // their inputs DISABLED — disabled fields neither validate nor submit,
      // and the strip/validate passes below skip them.
      this.conds = Array.from(
        this.querySelectorAll('[data-mst-cond-option], [data-mst-cond-not]')
      );
      // Groups whose ROW COUNT is dictated by a variant option ("Broj članova").
      this.countGroups = Array.from(this.querySelectorAll('[data-count-option]'));
      // Groups bounded by a RANGE option ("Broj cura bez mlade" = "3-8").
      this.rangeGroups = Array.from(this.querySelectorAll('[data-range-option]'));

      if (this.conds.length || this.countGroups.length || this.rangeGroups.length) {
        this.sectionId = this.formId.replace('product-form-', '');
        this.onVariantChangedBound = (e) => {
          const d = e.detail || {};
          if (d.sectionId && d.sectionId !== this.sectionId) return;
          this.syncConds(d.variant);
          this.syncCounts(d.variant);
          this.syncRanges(d.variant);
        };
        document.addEventListener('mst:variant:changed', this.onVariantChangedBound);
        const current = this.currentVariant();
        this.syncConds(current);
        this.syncCounts(current);
        this.syncRanges(current);
      }
    }

    /* A tier like "3-8" is what the customer actually paid for, so it becomes
       the group's real min/max — otherwise someone buys the 3–8 frame and
       types twenty names. */
    syncRanges(variant) {
      if (!variant || !this.rangeGroups) return;
      this.rangeGroups.forEach((group) => {
        const pos = parseInt(group.dataset.rangeOption, 10);
        const raw = String(variant.options[pos - 1] || '');
        const m = raw.match(/(\d+)\s*[-–—]\s*(\d+)/);
        if (!m) return;

        const lo = parseInt(m[1], 10);
        const hi = parseInt(m[2], 10);
        group.dataset.min = lo;
        group.dataset.max = hi;

        // gap1-2-10: names hidden by a smaller tier come back with a bigger
        // one, so count them in before clamping to the tier.
        const parked = group.querySelectorAll('[data-mst-row][data-mst-parked]').length;
        const wanted = this.activeRows(group).length + parked;
        this.setActiveCount(group, Math.min(Math.max(wanted, lo), hi));
        this.reindex(group);
        this.toggleAdd(group, hi, lo);
        this.updateCountNote(group, lo, hi);
      });
    }

    updateCountNote(group) {
      const note = group.querySelector('[data-mst-count-note]');
      if (!note) return;
      const lo = parseInt(group.dataset.min || '0', 10);
      const hi = parseInt(group.dataset.max || '99', 10);
      const rows = this.activeRows(group);
      const count = rows.length;
      // gap1-2-10: "Upisano N" counts names actually typed, not empty rows.
      const filled = rows.filter((row) => {
        const name = row.querySelector('input[type="text"]');
        return name && String(name.value || '').trim() !== '';
      }).length;

      let text = (note.dataset.template || '[count] / [lo]–[hi]')
        .replace('[count]', filled)
        .replace('[lo]', lo)
        .replace('[hi]', hi);

      // At the cap, say what to do about it — the tier is a variant, so more
      // girls means picking a bigger one, which is not obvious from a
      // greyed-out button alone.
      if (count >= hi && note.dataset.atMax) {
        text += ' ' + note.dataset.atMax;
      }
      note.textContent = text;
    }

    syncCounts(variant) {
      if (!variant || !this.countGroups) return;
      this.countGroups.forEach((group) => {
        const pos = parseInt(group.dataset.countOption, 10);
        // Option values like "bez djece" have no number — leave the rows alone.
        const wanted = parseInt(variant.options[pos - 1], 10);
        if (isNaN(wanted)) return;
        this.setRowCount(group, wanted);
      });
    }

    setRowCount(group, wanted) {
      const rows = group.querySelector('[data-mst-rows]');
      const tpl = group.querySelector('template');
      if (!rows || !tpl) return;

      const max = parseInt(group.dataset.max || '99', 10);
      const target = Math.min(Math.max(wanted, 1), max);

      this.setActiveCount(group, target);
      this.reindex(group);
      // Newly cloned rows carry a remove button — hide it like the rest.
      this.toggleAdd(group, max, parseInt(group.dataset.min || '1', 10));
    }

    /* ---- gap1-2-10: rows a smaller tier/count cannot hold -----------------
       A row with something typed in it is PARKED: hidden, inputs disabled
       (so it neither validates nor submits) instead of deleted, and it
       comes back, name and numbers included, when a bigger option is
       picked or "Dodaj" is pressed. Empty surplus rows are simply dropped.
       Parked rows always sit after the active ones, in their old order. */
    activeRows(group) {
      return Array.from(group.querySelectorAll('[data-mst-row]')).filter(
        (row) => !row.hasAttribute('data-mst-parked')
      );
    }

    rowHasText(row) {
      return Array.from(row.querySelectorAll('input')).some(
        (inp) =>
          (inp.type === 'text' || inp.type === 'number') &&
          String(inp.value || '').trim() !== ''
      );
    }

    parkRow(row) {
      row.setAttribute('data-mst-parked', '');
      row.hidden = true;
      row.querySelectorAll('input, select, textarea').forEach((inp) => {
        inp.disabled = true;
      });
    }

    unparkRow(group) {
      const row = group.querySelector('[data-mst-row][data-mst-parked]');
      if (!row) return null;
      row.removeAttribute('data-mst-parked');
      row.hidden = false;
      row.querySelectorAll('input, select, textarea').forEach((inp) => {
        inp.disabled = false;
      });
      return row;
    }

    appendRow(group) {
      const rows = group.querySelector('[data-mst-rows]');
      const tpl = group.querySelector('template');
      if (!rows || !tpl) return null;
      const node = tpl.content.firstElementChild.cloneNode(true);
      rows.appendChild(node);
      return node;
    }

    setActiveCount(group, target) {
      const active = this.activeRows(group);
      while (active.length < target) {
        const row = this.unparkRow(group) || this.appendRow(group);
        if (!row) break;
        active.push(row);
      }
      while (active.length > target) {
        const last = active.pop();
        if (this.rowHasText(last)) {
          this.parkRow(last);
        } else {
          last.remove();
        }
      }
      this.updateParkedNote(group);
    }

    updateParkedNote(group) {
      const note = group.querySelector('[data-mst-parked-note]');
      if (!note) return;
      const names = Array.from(
        group.querySelectorAll('[data-mst-row][data-mst-parked]')
      )
        .map((row) => {
          const name = row.querySelector('input[type="text"]');
          return name ? String(name.value || '').trim() : '';
        })
        .filter(Boolean);
      if (names.length) {
        note.textContent = fill(note.dataset.template || '[names]', {
          names: names.join(', ')
        });
        note.hidden = false;
      } else {
        note.textContent = '';
        note.hidden = true;
      }
    }

    announce(group, text) {
      const live = group.querySelector('[data-mst-live]');
      if (!live || !text) return;
      live.textContent = '';
      window.requestAnimationFrame(() => {
        live.textContent = text;
      });
    }

    disconnectedCallback() {
      if (this.onVariantChangedBound) {
        document.removeEventListener('mst:variant:changed', this.onVariantChangedBound);
      }
    }

    // Reads the section's variant picker directly (JSON + checked radios)
    // instead of its element methods — safe regardless of upgrade order.
    currentVariant() {
      const picker = document.querySelector(
        'mst-variant-picker[data-section="' + this.sectionId + '"]'
      );
      if (!picker) return null;
      let variants = [];
      const json = picker.querySelector('script[data-variants]');
      if (json) {
        try {
          variants = JSON.parse(json.textContent);
        } catch (e) {
          variants = [];
        }
      }
      const selected = [];
      picker.querySelectorAll('fieldset[data-option-position]').forEach((fs) => {
        const pos = parseInt(fs.dataset.optionPosition, 10);
        const checked = fs.querySelector('input[type="radio"]:checked');
        selected[pos - 1] = checked ? checked.value : null;
      });
      return (
        variants.find((v) =>
          selected.every((val, i) => !val || v.options[i] === val)
        ) || null
      );
    }

    syncConds(variant) {
      if (!variant) return; // unavailable combination — keep last state
      this.conds.forEach((el) => {
        const pos = parseInt(el.dataset.mstCondOption, 10);
        const current = variant.options[pos - 1];
        // data-mst-cond-not is the inverse: show for every value EXCEPT this
        // one (e.g. the wedding magnets take a date on every shape but
        // "Krug sa srcem"). Listing the positives instead would mean three
        // copies of the same field, and duplicate ids with it.
        const show = el.dataset.mstCondNot
          ? current !== el.dataset.mstCondNot
          : current === el.dataset.mstCondValue;
        el.hidden = !show;
        el.querySelectorAll('input, select, textarea').forEach((inp) => {
          // a parked name row stays disabled until its group brings it back
          if (show && inp.closest('[data-mst-parked]')) return;
          inp.disabled = !show;
        });
      });
    }

    initGroup(group) {
      const rows = group.querySelector('[data-mst-rows]');
      const addBtn = group.querySelector('[data-mst-add]');
      const tpl = group.querySelector('template');

      // Read the bounds on every use, never once at init: with range_option
      // the selected tier rewrites data-min/data-max, and a value captured
      // here would keep the original 3–20 — re-enabling the add button past
      // the tier's real cap and printing the wrong range in the counter.
      const bounds = () => ({
        min: parseInt(group.dataset.min || '0', 10),
        max: parseInt(group.dataset.max || '99', 10)
      });

      if (addBtn && tpl) {
        addBtn.addEventListener('click', () => {
          if (this.activeRows(group).length >= bounds().max) return;
          // A row parked by a smaller tier comes back first, name and all.
          const node = this.unparkRow(group) || this.appendRow(group);
          if (!node) return;
          this.reindex(group);
          this.toggleAdd(group);
          this.updateParkedNote(group);
          const f = node.querySelector('input, select, textarea');
          if (f) f.focus();
        });
      }

      rows.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-mst-remove]');
        if (!rm) return;
        const active = this.activeRows(group);
        if (active.length <= bounds().min) return;
        const row = rm.closest('[data-mst-row]');
        const labelEl = row.querySelector('[data-mst-rowlabel]');
        const removed = labelEl ? labelEl.textContent.trim() : '';
        // accessibility-08: focus stays in the list (the next row, else the
        // previous one, else the add button) and never falls to <body>.
        const at = active.indexOf(row);
        const neighbour = active[at + 1] || active[at - 1] || null;
        row.remove();
        this.reindex(group);
        this.toggleAdd(group);
        const target =
          (neighbour && neighbour.querySelector('input, select, textarea')) ||
          (addBtn && !addBtn.disabled ? addBtn : null);
        if (target) target.focus();
        this.announce(group, fill(group.dataset.removedTemplate || '', { label: removed }));
      });

      // The counter reports names actually typed, so follow the typing.
      if (group.dataset.rangeOption) {
        rows.addEventListener('input', () => this.updateCountNote(group));
      }

      this.reindex(group);
      this.toggleAdd(group);
    }

    toggleAdd(group) {
      const count = this.activeRows(group).length;
      const min = parseInt(group.dataset.min || '0', 10);
      const max = parseInt(group.dataset.max || '99', 10);
      const addBtn = group.querySelector('[data-mst-add]');
      if (addBtn) addBtn.disabled = count >= max;
      // Count driven by a variant option → no manual add/remove at all.
      const locked = !!group.dataset.countOption;
      group.querySelectorAll('[data-mst-remove]').forEach((b) => {
        b.style.visibility = locked || count <= min ? 'hidden' : 'visible';
      });
      if (group.dataset.rangeOption) this.updateCountNote(group);
    }

    reindex(group) {
      const prefix = group.dataset.group || 'Stavka';
      this.activeRows(group).forEach((row, idx) => {
        const i = idx + 1;
        const label = row.querySelector('[data-mst-rowlabel]');
        if (label) label.textContent = prefix + ' ' + i;
        // accessibility-08: "Ukloni: Cura 3", not twenty identical "Ukloni"
        const rm = row.querySelector('[data-mst-remove]');
        if (rm && group.dataset.removeTemplate) {
          rm.setAttribute('aria-label', fill(group.dataset.removeTemplate, { label: prefix + ' ' + i }));
        }
        row.querySelectorAll('[data-prop-base]').forEach((inp) => {
          const key = inp.dataset.propBase.replace('{i}', i);
          inp.name = 'properties[' + key + ']';
          // keep the accessible name in sync with the property key,
          // preserving the field-purpose suffix (e.g. "Cura 2 — Ime")
          const suffix = inp.dataset.labelSuffix || '';
          inp.setAttribute('aria-label', suffix ? key + ' — ' + suffix : key);
          if (this.formId) inp.setAttribute('form', this.formId);
        });
      });
    }

    initCounter(inp) {
      const wrap = inp.closest('.mst-field') || inp.parentElement;
      const out = wrap && wrap.querySelector('[data-mst-count]');
      const max = inp.getAttribute('maxlength');
      if (!out || !max) return;
      const upd = () => (out.textContent = inp.value.length + ' / ' + max);
      inp.addEventListener('input', upd);
      upd();
    }

    /* The shared error box says "fill the required fields" by default; a
       number out of range gets its own sentence naming the field. */
    showError(message) {
      if (!this.errorEl) return;
      const span = this.errorEl.querySelector('span');
      if (span) {
        if (this.defaultError == null) this.defaultError = span.textContent;
        span.textContent = message || this.defaultError;
      }
      this.errorEl.hidden = false;
    }

    numberMessage(inp) {
      let field = inp.getAttribute('aria-label') || '';
      if (!field && inp.labels && inp.labels[0]) {
        const clone = inp.labels[0].cloneNode(true);
        clone
          .querySelectorAll('[aria-hidden="true"], .visually-hidden')
          .forEach((n) => n.remove());
        field = clone.textContent.replace(/\s+/g, ' ').trim();
      }
      const min = inp.getAttribute('min') || '1';
      const max = inp.getAttribute('max');
      const tpl = max ? this.dataset.msgRange : this.dataset.msgMin;
      return fill(tpl || '', { field: field, min: min, max: max || '' });
    }

    onSubmit(e) {
      // 1) validate required personalizer fields
      let firstInvalid = null;
      this.querySelectorAll('[data-mst-required]').forEach((inp) => {
        if (inp.disabled) return; // hidden variant-conditional field
        const empty = !String(inp.value || '').trim();
        inp.classList.toggle('mst-invalid', empty);
        inp.setAttribute('aria-invalid', empty ? 'true' : 'false');
        if (empty && !firstInvalid) firstInvalid = inp;
      });
      let message = '';
      // 1b) gap1-2-04: form.noValidate is on, so min/max/step were never
      // enforced: a hairstyle 500, a table number -3 or a bow row of 0
      // went straight to the order. Every enabled number field (except the
      // quantity stepper, which clamps itself) must hold a whole number
      // inside its bounds; empty optional ones stay allowed.
      this.querySelectorAll('input[type="number"]').forEach((inp) => {
        if (inp.disabled || inp.name === 'quantity') return;
        // The ribbon builder validates its own rows, with its own messages.
        if (inp.closest('mst-ribbon-builder')) return;
        if (inp.hasAttribute('data-mst-required') && !String(inp.value || '').trim()) return;
        const raw = String(inp.value || '').trim();
        const bad = inp.validity.badInput || (raw !== '' && !inp.validity.valid);
        inp.classList.toggle('mst-invalid', bad);
        inp.setAttribute('aria-invalid', bad ? 'true' : 'false');
        if (bad && !firstInvalid) {
          firstInvalid = inp;
          message = this.numberMessage(inp);
        }
      });
      if (firstInvalid) {
        e.preventDefault();
        e.stopPropagation();
        this.showError(message);
        firstInvalid.focus();
        const reduced =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        firstInvalid.scrollIntoView({
          behavior: reduced ? 'auto' : 'smooth',
          block: 'center'
        });
        return;
      }
      if (this.errorEl) this.errorEl.hidden = true;
      // 2) strip empty OPTIONAL properties so the order stays clean
      this._disabled = [];
      this.querySelectorAll('input, select, textarea').forEach((inp) => {
        if (inp.disabled) return; // variant-conditional fields stay disabled
        if (
          inp.name &&
          inp.name.indexOf('properties[') === 0 &&
          !String(inp.value || '').trim()
        ) {
          inp.disabled = true;
          this._disabled.push(inp);
        }
      });
      // re-enable shortly after so the customer can keep editing
      setTimeout(() => (this._disabled || []).forEach((i) => (i.disabled = false)), 2000);
    }
  }

  if (!customElements.get('mst-personalizer')) {
    customElements.define('mst-personalizer', MstPersonalizer);
  }

  /* ------------------------------------------------------------------
     Shared helpers for the two quantity-aware personalizer blocks
     ------------------------------------------------------------------ */
  function money(cents) {
    if (window.MST && typeof window.MST.formatMoney === 'function') {
      return window.MST.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function fill(template, values) {
    // [square] placeholders, not {{ curly }} — Shopify consumes curly ones
    // server-side when the `t` filter runs.
    if (!template) return '';
    return template.replace(/\[(\w+)\]/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(values, key)
        ? String(values[key])
        : m;
    });
  }

  /* The unit price baked into data-unit-price is the variant selected at
     render time. On products where variants differ in price (e.g. the favour
     spoon costs more "S mašnicom"), a running total that ignores the picker
     shows the wrong number — so follow the variant. */
  function watchVariantPrice(el, formId, apply) {
    const sectionId = String(formId || '').replace('product-form-', '');
    el._onVariantPrice = (e) => {
      const d = e.detail || {};
      if (d.sectionId && d.sectionId !== sectionId) return;
      if (d.variant && typeof d.variant.price === 'number') apply(d.variant.price);
    };
    document.addEventListener('mst:variant:changed', el._onVariantPrice);
  }

  function unwatchVariantPrice(el) {
    if (el._onVariantPrice) {
      document.removeEventListener('mst:variant:changed', el._onVariantPrice);
    }
  }

  function slavicPlural(count, one, few, other) {
    const abs = Math.abs(count);
    const m10 = abs % 10;
    const m100 = abs % 100;
    if (m10 === 1 && m100 !== 11) return one;
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
    return other;
  }

  /* ------------------------------------------------------------------
     <mst-table-numbers> — quantity means "how many tables", and each
     piece carries its own number. Spells the range out so nobody orders
     5 expecting five identical markers.
     ------------------------------------------------------------------ */
  class MstTableNumbers extends HTMLElement {
    connectedCallback() {
      this.qty = this.querySelector('[data-tnum-qty]');
      this.rangeOut = this.querySelector('[data-tnum-range]');
      this.summary = this.querySelector('[data-tnum-summary]');
      this.startInput = this.querySelector('input[name="properties[Prvi broj]"]');
      this.unitPrice = parseInt(this.dataset.unitPrice || '0', 10);
      if (!this.qty) return;

      this.onChange = () => this.render();
      this.qty.addEventListener('change', this.onChange);
      this.qty.addEventListener('input', this.onChange);
      if (this.startInput) {
        this.startInput.addEventListener('change', this.onChange);
        this.startInput.addEventListener('input', this.onChange);
      }

      watchVariantPrice(this, this.dataset.formId, (price) => {
        this.unitPrice = price;
        this.render();
      });

      this.render();
    }

    disconnectedCallback() {
      if (this.qty && this.onChange) {
        this.qty.removeEventListener('change', this.onChange);
        this.qty.removeEventListener('input', this.onChange);
      }
      if (this.startInput && this.onChange) {
        this.startInput.removeEventListener('change', this.onChange);
        this.startInput.removeEventListener('input', this.onChange);
      }
      unwatchVariantPrice(this);
    }

    render() {
      const count = Math.max(1, parseInt(this.qty.value, 10) || 1);
      const start = Math.max(1, parseInt(this.startInput && this.startInput.value, 10) || 1);
      const end = start + count - 1;

      // Short runs read better spelled out; long ones as a range.
      let list;
      if (count === 1) {
        list = String(start);
      } else if (count <= 6) {
        list = Array.from({ length: count }, (_, i) => start + i).join(', ');
      } else {
        list = start + ' – ' + end;
      }

      if (this.rangeOut) {
        this.rangeOut.value = count === 1 ? String(start) : start + ' – ' + end;
      }

      if (this.summary) {
        const tpl = count === 1 ? this.dataset.summaryOne : this.dataset.summaryMany;
        let text = fill(tpl, { count: count, list: list, start: start, end: end });
        if (this.unitPrice) {
          text +=
            ' · ' +
            fill(this.dataset.totalLabel, { total: money(this.unitPrice * count) });
        }
        this.summary.textContent = text;
      }
    }
  }

  if (!customElements.get('mst-table-numbers')) {
    customElements.define('mst-table-numbers', MstTableNumbers);
  }

  /* ------------------------------------------------------------------
     <mst-bulk-hint> — quick amounts + running total for favours bought
     one per guest. Drives the real quantity input in buy-buttons.
     ------------------------------------------------------------------ */
  class MstBulkHint extends HTMLElement {
    connectedCallback() {
      const formId = this.dataset.formId || '';
      this.form = formId && document.getElementById(formId);
      this.qty = this.form && this.form.querySelector('input[name="quantity"]');
      this.totalOut = this.querySelector('[data-bulk-total]');
      this.unitPrice = parseInt(this.dataset.unitPrice || '0', 10);

      // No stepper on the page (quantity hidden by the merchant) — the quick
      // picks would have nothing to drive, so stay out of the way entirely.
      if (!this.qty) {
        this.hidden = true;
        return;
      }

      this.onPick = (e) => {
        const btn = e.target.closest('[data-bulk-amount]');
        if (!btn) return;
        e.preventDefault();
        this.qty.value = btn.dataset.bulkAmount;
        this.qty.dispatchEvent(new Event('change', { bubbles: true }));
        this.render();
      };
      this.addEventListener('click', this.onPick);

      this.onChange = () => this.render();
      this.qty.addEventListener('change', this.onChange);
      this.qty.addEventListener('input', this.onChange);

      watchVariantPrice(this, formId, (price) => {
        this.unitPrice = price;
        this.render();
      });

      this.render();
    }

    disconnectedCallback() {
      if (this.onPick) this.removeEventListener('click', this.onPick);
      if (this.qty && this.onChange) {
        this.qty.removeEventListener('change', this.onChange);
        this.qty.removeEventListener('input', this.onChange);
      }
      unwatchVariantPrice(this);
    }

    render() {
      const count = Math.max(1, parseInt(this.qty.value, 10) || 1);

      this.querySelectorAll('[data-bulk-amount]').forEach((b) => {
        const on = parseInt(b.dataset.bulkAmount, 10) === count;
        b.classList.toggle('is-active', on);
        // accessibility-08: the chosen amount was shown by colour only
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (this.totalOut && this.unitPrice) {
        const piece = slavicPlural(
          count,
          this.dataset.pieceOne,
          this.dataset.pieceFew,
          this.dataset.pieceOther
        );
        this.totalOut.textContent = fill(this.dataset.totalLabel, {
          count: count,
          piece: piece,
          total: money(this.unitPrice * count)
        });
      }
    }
  }

  if (!customElements.get('mst-bulk-hint')) {
    customElements.define('mst-bulk-hint', MstBulkHint);
  }

  /* ------------------------------------------------------------------
     <mst-bow-split> — "20 pink, 20 blue" bow colours for favour spoons.
     Quantity is DERIVED from the rows, so while this is showing it owns
     the number and the standard stepper is hidden; when the shopper
     switches back to "Bez mašnice" the stepper returns untouched.
     ------------------------------------------------------------------ */
  class MstBowSplit extends HTMLElement {
    connectedCallback() {
      const formId = this.dataset.formId || '';
      this.form = formId && document.getElementById(formId);
      this.qty = this.form && this.form.querySelector('input[name="quantity"]');
      this.qtyWrap = this.qty && this.qty.closest('.mst-product-form__quantity');
      this.rowsEl = this.querySelector('[data-bow-rows]');
      this.tpl = this.querySelector('[data-bow-template]');
      this.addBtn = this.querySelector('[data-bow-add]');
      this.totalOut = this.querySelector('[data-bow-total]');
      this.propOut = this.querySelector('[data-bow-prop]');
      this.unitPrice = parseInt(this.dataset.unitPrice || '0', 10);
      this.maxRows = parseInt(this.dataset.maxRows || '4', 10);
      if (!this.rowsEl || !this.tpl) return;

      // The .mst-pers__cond wrapper the personalizer toggles for us.
      this.cond = this.closest('[data-mst-cond-option]');

      this.onAdd = () => {
        if (this.rowsEl.querySelectorAll('[data-bow-row]').length >= this.maxRows) return;
        this.touched = true;
        const row = this.addRow();
        this.render();
        const sel = row && row.querySelector('[data-bow-color]');
        if (sel) sel.focus();
      };
      if (this.addBtn) this.addBtn.addEventListener('click', this.onAdd);

      this.onRows = (e) => {
        const rm = e.target.closest('[data-bow-remove]');
        if (e.type !== 'click' || rm) this.touched = true;
        if (rm) {
          const all = [...this.rowsEl.querySelectorAll('[data-bow-row]')];
          if (all.length <= 1) return;
          const row = rm.closest('[data-bow-row]');
          // accessibility-08: keep focus in the list, never on <body>
          const at = all.indexOf(row);
          const neighbour = all[at + 1] || all[at - 1] || null;
          row.remove();
          this.render();
          const target =
            (neighbour && neighbour.querySelector('[data-bow-color]')) ||
            (this.addBtn && !this.addBtn.disabled ? this.addBtn : null);
          if (target) target.focus();
          return;
        }
        this.render();
      };
      this.rowsEl.addEventListener('click', this.onRows);
      this.rowsEl.addEventListener('change', this.onRows);
      this.rowsEl.addEventListener('input', this.onRows);

      // React to the personalizer showing/hiding our wrapper on variant change.
      if (this.cond && 'MutationObserver' in window) {
        this.obs = new MutationObserver(() => this.render());
        this.obs.observe(this.cond, { attributes: true, attributeFilter: ['hidden'] });
      }

      // "S mašnicom" is a pricier variant than "Bez mašnice" — follow it.
      watchVariantPrice(this, formId, (price) => {
        this.unitPrice = price;
        this.render();
      });

      this.addRow();
      this.render();
    }

    disconnectedCallback() {
      if (this.addBtn && this.onAdd) this.addBtn.removeEventListener('click', this.onAdd);
      if (this.rowsEl && this.onRows) {
        this.rowsEl.removeEventListener('click', this.onRows);
        this.rowsEl.removeEventListener('change', this.onRows);
        this.rowsEl.removeEventListener('input', this.onRows);
      }
      if (this.obs) this.obs.disconnect();
      unwatchVariantPrice(this);
      this.restoreQuantity();
    }

    addRow() {
      const node = this.tpl.content.firstElementChild.cloneNode(true);
      // Pre-select the next unused colour so adding a row is one click.
      const used = this.usedColors();
      const sel = node.querySelector('[data-bow-color]');
      if (sel) {
        const free = [...sel.options].find((o) => used.indexOf(o.value) === -1);
        if (free) sel.value = free.value;
      }
      this.rowsEl.appendChild(node);
      return node;
    }

    usedColors() {
      return [...this.rowsEl.querySelectorAll('[data-bow-color]')].map((s) => s.value);
    }

    rows() {
      return [...this.rowsEl.querySelectorAll('[data-bow-row]')].map((row) => ({
        color: row.querySelector('[data-bow-color]').value,
        qty: Math.max(1, parseInt(row.querySelector('[data-bow-qty]').value, 10) || 1)
      }));
    }

    isActive() {
      return !(this.cond && this.cond.hidden);
    }

    restoreQuantity() {
      if (this.qtyWrap) this.qtyWrap.hidden = false;
    }

    render() {
      const rows = this.rows();
      const count = rows.length;

      if (this.addBtn) this.addBtn.disabled = count >= this.maxRows;
      this.rowsEl.querySelectorAll('[data-bow-remove]').forEach((b) => {
        b.style.visibility = count <= 1 ? 'hidden' : 'visible';
      });
      // accessibility-08: every row repeated the same three names; number
      // them ("Stavka 2 — Boja mašnice", "Ukloni stavku 2").
      this.rowsEl.querySelectorAll('[data-bow-row]').forEach((row, idx) => {
        const n = idx + 1;
        const prefix = fill(this.dataset.rowLabel || '', { n: n });
        row.querySelectorAll('[data-bow-color], [data-bow-qty]').forEach((field) => {
          if (!field.dataset.baseLabel) field.dataset.baseLabel = field.getAttribute('aria-label') || '';
          field.setAttribute('aria-label', prefix ? prefix + ' — ' + field.dataset.baseLabel : field.dataset.baseLabel);
        });
        const rm = row.querySelector('[data-bow-remove]');
        if (rm && this.dataset.removeLabel) {
          rm.setAttribute('aria-label', fill(this.dataset.removeLabel, { n: n }));
        }
      });

      if (!this.isActive()) {
        // "Bez mašnice" — hand the stepper back and stop driving quantity.
        this.restoreQuantity();
        this.wasActive = false;
        return;
      }
      // gap1-2-04: switching to "S mašnicom" after picking e.g. 100 used to
      // reset the order to the default row of 20. While the rows are
      // untouched, the single row takes over a quantity the shopper chose
      // (anything above 1) instead — read before it gets overwritten below.
      if (this.wasActive === false && !this.touched && this.qty) {
        const qtyInputs = this.rowsEl.querySelectorAll('[data-bow-qty]');
        const chosen = parseInt(this.qty.value, 10);
        const cap = parseInt((qtyInputs[0] && qtyInputs[0].getAttribute('max')) || '1000', 10);
        if (qtyInputs.length === 1 && chosen > 1 && chosen <= cap) {
          qtyInputs[0].value = chosen;
          rows[0].qty = chosen;
        }
      }
      this.wasActive = true;
      if (this.qtyWrap) this.qtyWrap.hidden = true;

      const total = rows.reduce((sum, r) => sum + r.qty, 0);
      const summary = rows.map((r) => r.qty + '× ' + r.color).join(', ');

      if (this.propOut) this.propOut.value = summary;

      if (this.qty && parseInt(this.qty.value, 10) !== total) {
        this.qty.value = total;
        this.qty.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.totalOut) {
        const piece = slavicPlural(
          total,
          this.dataset.pieceOne,
          this.dataset.pieceFew,
          this.dataset.pieceOther
        );
        this.totalOut.textContent = fill(this.dataset.totalLabel, {
          count: total,
          piece: piece,
          total: money(this.unitPrice * total)
        });
      }
    }
  }

  if (!customElements.get('mst-bow-split')) {
    customElements.define('mst-bow-split', MstBowSplit);
  }
})();
